import { baseApi } from 'src/Services/api.ts'
import { PaginatedResponse } from 'src/Redux/types.ts'
import { ModelType } from 'src/Redux/api/modelTypesApi.ts'

import { DriveTypeEnum } from './equipmentApi.ts'

export interface ModelImage {
  id: number
  modelId: number
  url: string
  createdAt: string
}

export interface Model {
  baseRentalCost: number
  count: number
  createdAt: string
  driveType: DriveTypeEnum
  engineCapacity: number
  fuelEfficiencySummer: number
  fuelEfficiencyWinter: number
  id: number
  imageUrl: string
  imgBase63: string
  name: string
  modelType: ModelType
  modelTypeId: number
  modelTypeName: string
  newestEquipmentYear?: number
  description?: string
  operationalLogs?: string
  images?: ModelImage[]
  modelTools?: ModelTool[]
}

export interface ModelTool {
  id: number
  name: string
  toolWorkPrice: number
  description?: string | null
  isDefault?: boolean
  images?: string[]
}

export interface CreateModelBody {
  baseRentalCost: number
  driveType?: DriveTypeEnum
  engineCapacity: number
  fuelEfficiencySummer: number
  fuelEfficiencyWinter: number
  imageUrl?: string
  name: string
  modelTypeId: number
  description?: string
  image?: File
  operationalLogs?: string
}

export interface ModelQueryParams {
  pageNumber?: number
  limit?: number
  searchString?: string
  all?: boolean
}

export const modelApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllModels: builder.query<
      PaginatedResponse<Model[]>,
      ModelQueryParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams()

        if (params) {
          if (params.pageNumber)
            queryParams.append('page', params.pageNumber.toString())
          if (params.limit) queryParams.append('limit', params.limit.toString())
          if (params.searchString)
            queryParams.append('searchTerm', params.searchString)
          if (params.all) queryParams.append('all', 'true')
        }

        const queryString = queryParams.toString()
        return `/models${queryString ? `?${queryString}` : ''}`
      },
      providesTags: ['models'],
    }),

    addModel: builder.mutation<Model, CreateModelBody>({
      query: (data) => {
        const formData = new FormData()
        Object.entries(data).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (key === 'image' && value instanceof File) {
              formData.append(key, value)
            } else {
              formData.append(key, String(value))
            }
          }
        })
        return {
          url: `/models`,
          method: 'POST',
          body: formData,
        }
      },
      invalidatesTags: ['models'],
    }),

    addModelImages: builder.mutation<
      ModelImage[],
      { modelId: number; images: File[] }
    >({
      query: ({ modelId, images }) => {
        const formData = new FormData()
        images.forEach((image) => formData.append('images', image))
        return {
          url: `/models/${modelId}/images`,
          method: 'POST',
          body: formData,
        }
      },
      invalidatesTags: ['models'],
    }),

    deleteModelImage: builder.mutation<
      void,
      { modelId: number; imageId: number }
    >({
      query: ({ modelId, imageId }) => ({
        url: `/models/${modelId}/images/${imageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['models'],
    }),

    setModelDefaultTool: builder.mutation<
      void,
      { modelId: number; toolId: number | null }
    >({
      query: ({ modelId, toolId }) => ({
        url: `/models/${modelId}/default-tool`,
        method: 'POST',
        body: { toolId },
      }),
      invalidatesTags: ['models', 'transport', 'tools'],
    }),
  }),
  overrideExisting: true,
})

export const {
  useGetAllModelsQuery,
  useAddModelMutation,
  useAddModelImagesMutation,
  useDeleteModelImageMutation,
  useSetModelDefaultToolMutation,
} = modelApi
